import {ComboboxInput, ComboboxItem, ComboboxPopover} from '@/components/ui/combobox-atoms/ComboboxAtoms'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {InputHelpText} from '@/components/ui/input-help-text/InputHelpText'
import {InputStatusIcon} from '@/components/ui/input-status-icon/InputStatusIcon'
import {Label} from '@/components/ui/label/Label'
import {SelectComboboxList, SelectVirtualList} from '@/components/ui/select-atoms/SelectAtoms'
import {Skeleton} from '@/components/ui/skeleton/Skeleton'
import {useTranslation} from '@/translations/i18n'
import {ComboboxProvider} from '@ariakit/react'
import {useMemo, useRef, useState} from 'react'
import {UserWorkArea} from '@/features/users/types'
import {
    StyledUserWorkAreasMultiSelectCheckbox,
    StyledUserWorkAreasMultiSelectCheckboxWrapper,
    StyledUserWorkAreasMultiSelectOption,
    StyledWorkAreaProvinceAcronym,
    StyledWorkAreasChip,
    StyledWorkAreasChipsContainer
} from './style'
import {Badge} from '@/components/ui/badge/Badge'
import {XCloseIcon} from '@/components/ui/icon/Icon'
import {useGetWorkAreas} from '@/features/users/queries/useGetWorkAreas'
import {Button} from '@/components/ui/button/Button'

interface UserWorkAreasMultiSelectProps {
    errorMessage?: string
    placeholder?: string
    values: UserWorkArea[]
    onChange: (value: UserWorkArea) => void
}

export const UserWorkAreasMultiSelect = ({
    errorMessage,
    placeholder,
    values,
    onChange
}: UserWorkAreasMultiSelectProps) => {
    const {t} = useTranslation()
    const [isQueryEnabled, setIsQueryEnabled] = useState(false)
    const comboboxInputRef = useRef(null)
    const workAreas = useGetWorkAreas(
        {
            limit: 20,
            responseType: 'extended'
        },
        {enabled: isQueryEnabled}
    )

    const selectedWorkAreasId = useMemo(() => (values?.length > 0 ? values.map(value => value.id) : []), [workAreas])

    return (
        <ComboboxProvider setValue={workAreas.onSearch} setOpen={isOpen => setIsQueryEnabled(isOpen)} resetValueOnHide>
            <Flexbox direction="column" gap={1.5} fullWidth>
                <Label>{t('users:work_areas')}</Label>
                <Flexbox direction="column" gap={4} fullWidth>
                    <Flexbox direction="column" gap={1.5} fullWidth>
                        <ComboboxInput
                            hasCloseCombobox
                            errorMessage={errorMessage}
                            ref={comboboxInputRef}
                            placeholder={placeholder || t('commons:search_placeholder')}
                        />
                        {!!errorMessage && (
                            <Flexbox align="center" gap={1.5}>
                                <InputStatusIcon hasError={true} />
                                <InputHelpText error={errorMessage} />
                            </Flexbox>
                        )}
                    </Flexbox>
                    <StyledWorkAreasChipsContainer gap={2}>
                        {values.map(value => (
                            <StyledWorkAreasChip key={value.id} variant="neutral" shape="rectangle">
                                {value?.area?.name}
                                <Button variant="ghost" shape="text" onClick={() => onChange(value)}>
                                    <XCloseIcon />
                                </Button>
                            </StyledWorkAreasChip>
                        ))}
                    </StyledWorkAreasChipsContainer>
                </Flexbox>

                <ComboboxPopover>
                    <SelectComboboxList>
                        {workAreas.isLoading ? (
                            <Flexbox direction="column" gap={2} fullWidth>
                                {Array.from({length: 6}, (_, index) => (
                                    <Flexbox key={index} gap={2} fullWidth>
                                        <Skeleton width={20} height={20} />
                                        <Skeleton width="100%" height={32} />
                                    </Flexbox>
                                ))}
                            </Flexbox>
                        ) : workAreas.isError ? (
                            <ComboboxItem disabled>
                                <InputHelpText error={t('errors:default')} />
                            </ComboboxItem>
                        ) : workAreas.remappedData.length > 0 ? (
                            <SelectVirtualList
                                data={workAreas.remappedData}
                                totalCount={workAreas.remappedData.length}
                                overscan={100}
                                endReached={() => workAreas.hasNextPage && workAreas.fetchNextPage()}
                                itemContent={(itemIndex, item) => (
                                    <StyledUserWorkAreasMultiSelectOption
                                        gap={2}
                                        align="center"
                                        fullWidth
                                        key={itemIndex}
                                    >
                                        <StyledUserWorkAreasMultiSelectCheckboxWrapper
                                            variant="ghost"
                                            shape="text"
                                            onClick={() => onChange(item)}
                                        >
                                            <StyledUserWorkAreasMultiSelectCheckbox
                                                id={item.id}
                                                checked={selectedWorkAreasId.includes(item.id)}
                                                onChange={() => onChange(item)}
                                            />
                                            <Flexbox justify="space-between" fullWidth>
                                                <Flexbox gap={2}>
                                                    {item.area.name}{' '}
                                                    {item.area?.provinceAcronym && (
                                                        <StyledWorkAreaProvinceAcronym>
                                                            {item.area.provinceAcronym}
                                                        </StyledWorkAreaProvinceAcronym>
                                                    )}
                                                </Flexbox>

                                                {item.areaType == 'neighborhood' && (
                                                    <Badge size="sm" shape="rectangle">
                                                        {t('commons:neighbourhood')}
                                                    </Badge>
                                                )}
                                            </Flexbox>
                                        </StyledUserWorkAreasMultiSelectCheckboxWrapper>
                                    </StyledUserWorkAreasMultiSelectOption>
                                )}
                            />
                        ) : (
                            <ComboboxItem disabled>
                                <InputHelpText helpText={t('commons:no_results')} />
                            </ComboboxItem>
                        )}
                    </SelectComboboxList>
                </ComboboxPopover>
            </Flexbox>
        </ComboboxProvider>
    )
}
